import type { AgentPatch } from '@tunasong/agent-tools'
import { logger } from '@tunasong/models'
import { getLiveEditor, isProposalApplied, markProposalApplied } from '@tunasong/plugin-lib'
import type { TunaEditor } from '@tunasong/plugin-lib'
import type { Comment, Entity, Persisted } from '@tunasong/schemas'
import { getCommandSpec, isProposalComment } from '@tunasong/schemas'
import { applyProposal } from './proposal-apply.js'

export interface ProposalResolution {
  status: 'accepted' | 'rejected'
  at: string
  by: string
}

/**
 * Metadata shape on a proposal Comment. The `anchorBlockId` + `blockSpan`
 * fields are derived server-side at propose-time (see `derive-anchor.ts`
 * in ai-api) and let the gutter chip find proposals by block id without
 * re-walking patch paths. Legacy proposals that pre-date this addition
 * gracefully degrade (gutter skips them; the panel still surfaces them).
 */
export interface ProposalMetadata {
  [key: string]: unknown
  agentProposal?: boolean
  targetEntityId?: string
  summary?: string
  patch?: AgentPatch
  resolution?: ProposalResolution
  anchorBlockId?: string
  blockSpan?: number
}

/** A proposal Comment narrowed to carry proposal metadata. */
export interface ProposalComment extends Persisted<Comment> {
  metadata?: ProposalMetadata
}

/**
 * True when the comment is a proposal (vs. a regular chat message).
 * Delegates to the Task 1 kind-first guard (falls back to the legacy
 * `metadata.agentProposal` flag for comments written before `kind` existed).
 */
export const isProposal = (c: Persisted<Comment>): c is ProposalComment => isProposalComment(c)

/** True when the proposal hasn't been accepted or rejected yet. */
export const isPendingProposal = (p: ProposalComment): boolean => !p.metadata?.resolution

/**
 * True when the proposal carries an editor-command patch whose registered
 * spec is marked isDestructive. Drives focus-default + color-token
 * affordances in the proposal UI.
 */
export const isDestructiveProposal = (proposal: ProposalComment): boolean => {
  const patch = proposal.metadata?.patch
  if (!patch || patch.kind !== 'editor-command') {
    return false
  }
  return getCommandSpec(patch.commandId)?.isDestructive === true
}

/**
 * Dependencies for accept / reject. The two helpers are pure-ish: they
 * call into `applyProposal` (which mutates the editor or dispatches a
 * registered command) and write the resolution stamp via `updateComment`.
 *
 * Split from the React layer so callers stay pure. The gutter chip
 * popover and the side-panel proposals header share the same resolve
 * path — no logic duplication, single audit trail.
 */
export interface ResolveContext {
  /** Editor for the proposal's target entity, when mounted. Required for slate-ops + editor-command patches. */
  editor?: TunaEditor | null
  /** Current user id, stamped onto the resolution. */
  userId?: string
  /** Write a partial update to the proposal Comment (stamps the resolution). */
  updateComment: (id: string, partial: Partial<Persisted<Comment>>) => unknown
  /** Write a partial update to a target entity (for entity-metadata patches). */
  updateEntity: (id: string, partial: Partial<Persisted<Entity>>) => unknown
}

/**
 * Outcome of an accept attempt. `ok: false` carries a human-readable
 * `reason` so the surface (gutter chip / header / list) can show WHY the
 * apply failed instead of silently no-op'ing. `reason` is the thrown
 * error's message verbatim (applyProposal throws descriptive errors:
 * "No applicator registered for command: X", "anchor block not found: Y",
 * "editor is not mounted", etc.) — surface it so a dead Accept button is
 * never silent again.
 */
export type AcceptResult = { ok: true } | { ok: false; reason: string }

const stampResolution = (proposal: ProposalComment, status: 'accepted' | 'rejected', ctx: ResolveContext): void => {
  const resolution: ProposalResolution = {
    status,
    at: new Date().toISOString(),
    by: ctx.userId ?? 'unknown',
  }
  void ctx.updateComment(proposal.id, {
    metadata: { ...proposal.metadata, resolution },
  } as Partial<Persisted<Comment>>)
}

/**
 * Accept a proposal: run the patch against the editor / entity, then
 * stamp `metadata.resolution = { status: 'accepted', at, by }` on the
 * proposal Comment.
 *
 * Returns `{ ok: true }` when the patch applied cleanly. On failure (no
 * patch / target; applyProposal throws), logs a warning AND returns
 * `{ ok: false, reason }` so the caller can surface the cause on the
 * proposal instead of leaving a dead, silent Accept button.
 */
export const acceptProposal = (proposal: ProposalComment, ctx: ResolveContext): AcceptResult => {
  const { patch, targetEntityId } = proposal.metadata ?? {}
  if (!patch || !targetEntityId) {
    logger.warn('proposal-resolve: accept on proposal without patch or targetEntityId', proposal.id)
    return { ok: false, reason: 'This proposal has no applicable change (missing patch or target).' }
  }
  // Resolve the editor. Surfaces inside the document's TunaEditorContext supply
  // it via ctx.editor; the agent sidebar panel is OUTSIDE that context, so fall
  // back to the live-editor registry keyed by the target entity. slate-ops and
  // editor-command patches need a live editor — when the target doc isn't open,
  // report a friendly, actionable reason instead of a raw "not mounted" throw.
  const editor = ctx.editor ?? getLiveEditor(targetEntityId) ?? null
  if (!editor && (patch.kind === 'slate-ops' || patch.kind === 'editor-command')) {
    return { ok: false, reason: 'Open this document to apply this change.' }
  }
  /**
   * A doc edit is not idempotent: accepting it twice adds the section twice. The resolve PATCH can be lost (tab
   * closed right after Accept), so a proposal can come back pending after its change landed. The applied marker in
   * the doc says whether it did (GH #2476). An entity-metadata patch sets values, so applying it twice is harmless.
   */
  const isDocEdit = patch.kind === 'slate-ops' || patch.kind === 'editor-command'
  if (isDocEdit && isProposalApplied(editor, proposal.id)) {
    stampResolution(proposal, 'accepted', ctx)
    return { ok: true }
  }
  try {
    applyProposal(
      { targetEntityId, patch },
      {
        editor,
        updateEntity: (id, partial) => {
          void ctx.updateEntity(id, partial as Partial<Persisted<Entity>>)
        },
      }
    )
    if (isDocEdit) {
      markProposalApplied(editor, proposal.id)
    }
    stampResolution(proposal, 'accepted', ctx)
    return { ok: true }
  } catch (error) {
    logger.warn('proposal-resolve: apply failed', error)
    const reason = error instanceof Error ? error.message : 'Could not apply this change.'
    return { ok: false, reason }
  }
}

/**
 * Reject a proposal: stamp `metadata.resolution = { status: 'rejected',
 * at, by }` without applying the patch. The proposal stays in history
 * (the panel's "resolved" view); the gutter chip vanishes.
 */
export const rejectProposal = (proposal: ProposalComment, ctx: ResolveContext): void => {
  stampResolution(proposal, 'rejected', ctx)
}

/**
 * Record the inline ghost's decision on the persisted proposal behind it (GH #2677). The ghost applies an accepted
 * edit itself, so this never applies it again: it marks the doc applied (so a later Accept from the gutter is a no-op)
 * and stamps the resolution. A proposal already resolved elsewhere meanwhile (the gutter chip) is left as it is.
 */
export const recordInlineResolution = (
  proposal: ProposalComment,
  status: 'accepted' | 'rejected',
  ctx: ResolveContext
): void => {
  if (!isPendingProposal(proposal)) {
    return
  }
  if (status === 'accepted' && ctx.editor) {
    markProposalApplied(ctx.editor, proposal.id)
  }
  stampResolution(proposal, status, ctx)
}
